import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Hotel, Ticket, Stamp, ArrowRight, Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';

// "Complete your trip" cross-sell strip on the flight results page. Surfaces the OTHER products
// we offer for the searched destination — Hotels & Experiences (live), plus (international only)
// a "Book your visa" card that's a coming-soon placeholder, ready to wire to the Visa API once
// the key lands. eSIM is handled by its own <EsimCrossSell> just above this strip.
export const TripCrossSell = ({ destinationCity, destinationCountry, destinationCountryCode, destinationCode, departDate, returnDate, adults, children }) => {
  const navigate = useNavigate();
  const city = destinationCity || 'your destination';
  const isInternational = !!destinationCountryCode && destinationCountryCode !== 'IN';

  // Carry the trip's dates + travellers into the hotel search so check-in/out already match.
  // One-way → default a 3-night stay from the departure date.
  const guests = Math.max(1, (Number(adults) || 1) + (Number(children) || 0));
  let ci = departDate || '';
  let co = returnDate || '';
  if (ci && !co) {
    const d = new Date(ci);
    if (!isNaN(d)) { d.setDate(d.getDate() + 3); co = d.toISOString().slice(0, 10); }
  }
  const hotelParams = new URLSearchParams({ destination_code: destinationCity || destinationCode || '' });
  if (destinationCode) hotelParams.set('dest_iata', destinationCode);
  if (destinationCountry) hotelParams.set('country', destinationCountry);
  if (destinationCountryCode) hotelParams.set('country_code', destinationCountryCode);
  if (ci) hotelParams.set('ci', ci);
  if (co) hotelParams.set('co', co);
  hotelParams.set('g', String(guests));

  const cards = [
    {
      key: 'hotels', icon: Hotel, title: `Hotels in ${city}`, sub: 'Compare live stays for your dates',
      cta: 'Find hotels', onClick: () => navigate(`/hotels?${hotelParams.toString()}`),
      testid: 'crosssell-hotels',
    },
    {
      key: 'experiences', icon: Ticket, title: `Things to do in ${city}`, sub: 'Tours, tickets & activities',
      cta: 'Explore experiences', onClick: () => navigate(`/experiences?destination_code=${encodeURIComponent(destinationCode || destinationCity || '')}`),
      testid: 'crosssell-experiences',
    },
  ];
  if (isInternational) {
    cards.push({
      key: 'visa', icon: Stamp, title: `Visa for ${city}`, sub: 'Check requirements & apply',
      cta: 'Coming soon', soon: true, onClick: () => navigate('/visa'),
      testid: 'crosssell-visa',
    });
  }

  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-4 mb-4" data-testid="trip-crosssell">
      <p className="text-sm font-semibold flex items-center gap-1.5 mb-3">
        <Sparkles className="h-4 w-4 text-accent" /> Complete your trip to {city}
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <button key={c.key} onClick={c.onClick} data-testid={c.testid}
            className="group text-left rounded-xl border border-border bg-card p-3 flex items-center gap-3 card-elevate focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none">
            <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/12 text-accent shrink-0"><c.icon className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="block text-sm font-semibold truncate">{c.title}</span>
                {c.soon && <Badge variant="outline" className="rounded-full text-[9px] px-1.5 py-0 shrink-0">Soon</Badge>}
              </span>
              <span className="block text-xs text-muted-foreground truncate">{c.sub}</span>
              <span className="mt-0.5 inline-flex items-center gap-0.5 text-xs font-medium text-accent group-hover:gap-1.5 transition-[gap]">{c.cta} <ArrowRight className="h-3 w-3" /></span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
